"use client";
import { useRef, useState, useEffect } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,600;1,400&family=Outfit:wght@300;400;500;600&display=swap');

  .app-sec {
    width: 100%;
    background: #fff;
    padding: 40px 24px 96px;
    font-family: 'Outfit', sans-serif;
    box-sizing: border-box;
  }

  .app-sec-inner {
    max-width: 1100px;
    margin: 0 auto;
  }

  .app-sec-label {
    font-size: clamp(30px, 4vw, 46px);
    letter-spacing: -1px;
    text-transform: uppercase;
    color: #111;
    font-weight: 800;
    font-family: 'Outfit', sans-serif;
    margin-bottom: 28px;
    line-height: 1;
  }

  /* Main card */
  .app-main-card {
    background: #fff;
    border-radius: 28px;
    box-shadow:
      0 2px 4px rgba(0,0,0,0.04),
      0 8px 24px rgba(0,0,0,0.07),
      0 32px 64px rgba(0,0,0,0.08);
    overflow: hidden;
    display: grid;
    grid-template-columns: 1fr 400px;
    border: 1px solid rgba(0,0,0,0.06);
  }

  /* Left */
  .app-card-left {
    padding: 60px 56px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
  }

  .app-card-left::after {
    content: '';
    position: absolute;
    right: 0; top: 48px; bottom: 48px;
    width: 1px;
    background: linear-gradient(to bottom, transparent, #e8e8e8 20%, #e8e8e8 80%, transparent);
  }

  .app-headline {
    font-family: 'Lora', serif;
    font-size: clamp(36px, 4vw, 54px);
    font-weight: 600;
    color: #111;
    line-height: 1.15;
    letter-spacing: -1px;
    margin-bottom: 20px;
  }

  .app-headline em {
    font-style: italic;
    color: #F59A5F;
  }

  .app-desc {
    font-size: 17px;
    font-weight: 300;
    line-height: 1.8;
    color: #666;
    max-width: 420px;
    margin-bottom: 44px;
  }

  .app-desc strong {
    color: #333;
    font-weight: 500;
  }

  /* Download buttons */
  .app-btn-group {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 48px;
  }

  .app-dl-btn {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
    padding: 14px 24px;
    border-radius: 14px;
    transition: all 0.25s ease;
    border: 1.5px solid transparent;
    cursor: pointer;
    min-width: 172px;
  }

  .app-dl-btn.ios {
    background: #111;
    color: #fff;
    box-shadow: 0 4px 16px rgba(0,0,0,0.18);
  }

  .app-dl-btn.ios:hover {
    background: #222;
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(0,0,0,0.24);
  }

  .app-dl-btn.android {
    background: #fff;
    color: #111;
    border-color: #ddd;
    box-shadow: 0 4px 16px rgba(0,0,0,0.06);
  }

  .app-dl-btn.android:hover {
    border-color: #F59A5F;
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(245,154,95,0.15);
    color: #F59A5F;
  }

  .app-dl-btn.android:hover .btn-svg { fill: #F59A5F; }

  .btn-svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    display: block;
    transition: fill 0.25s;
  }

  .ios .btn-svg { fill: #fff; }
  .android .btn-svg { fill: #111; }

  .btn-copy { display: flex; flex-direction: column; gap: 1px; }
  .btn-copy-small { font-size: 10px; font-weight: 400; opacity: 0.6; letter-spacing: 0.3px; }
  .btn-copy-main { font-size: 15px; font-weight: 600; letter-spacing: -0.2px; }

  /* Trust strip */
  .app-trust-strip {
    display: flex;
    gap: 28px;
    padding-top: 28px;
    border-top: 1px solid #f0f0f0;
    flex-wrap: wrap;
  }

  .app-trust-item {
    display: flex;
    align-items: center;
    gap: 9px;
  }

  .app-trust-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: #FFF8F2;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
    border: 1px solid #FFD4B8;
  }

  .app-trust-text strong {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #111;
    line-height: 1;
    margin-bottom: 2px;
  }

  .app-trust-text span {
    font-size: 11px;
    color: #9CA3AF;
    font-weight: 300;
  }

  /* Right — phone */
  .app-card-right {
    background: linear-gradient(155deg, #FFFAF6 0%, #FFF3EB 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 52px 40px;
    position: relative;
    overflow: hidden;
  }

  .app-card-right::before {
    content: '';
    position: absolute;
    width: 320px; height: 320px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(245,154,95,0.1) 0%, transparent 70%);
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
  }

  .app-phone-wrap { position: relative; z-index: 2; }

  .app-phone-shell {
    width: 210px;
    height: 430px;
    background: linear-gradient(160deg, #fff 0%, #f7f7f7 100%);
    border-radius: 40px;
    border: 1.5px solid #ddd;
    box-shadow:
      0 48px 96px rgba(0,0,0,0.14),
      0 8px 24px rgba(0,0,0,0.07),
      inset 0 1px 0 rgba(255,255,255,0.9);
    padding: 10px;
    position: relative;
  }

  .app-phone-notch {
    position: absolute;
    top: 10px; left: 50%;
    transform: translateX(-50%);
    width: 64px; height: 9px;
    background: #ddd;
    border-radius: 8px;
    z-index: 5;
  }

  .app-phone-screen {
    width: 100%; height: 100%;
    border-radius: 32px;
    background: #fff;
    overflow: hidden;
    border: 1px solid #eee;
  }

  .phone-header {
    padding: 22px 14px 10px;
    border-bottom: 1px solid #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .phone-logo {
    font-family: 'Lora', serif;
    font-size: 15px;
    font-weight: 600;
    color: #111;
  }

  .phone-logo span { color: #F59A5F; }

  .phone-dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: #F59A5F;
  }

  .phone-search {
    margin: 10px 10px 0;
    background: #f7f7f7;
    border-radius: 8px;
    padding: 7px 10px;
    font-size: 9px;
    color: #bbb;
    font-family: 'Outfit', sans-serif;
    border: 1px solid #eee;
  }

  .phone-tags {
    display: flex;
    gap: 5px;
    padding: 8px 10px 0;
    overflow: hidden;
  }

  .phone-tag {
    background: #FFF5EE;
    border: 1px solid #FFD4B5;
    color: #F59A5F;
    font-size: 8px;
    font-weight: 500;
    padding: 3px 8px;
    border-radius: 20px;
    white-space: nowrap;
    font-family: 'Outfit', sans-serif;
  }

  .phone-products {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    padding: 8px 10px;
  }

  .phone-product {
    background: #fafafa;
    border-radius: 10px;
    padding: 8px 7px;
    border: 1px solid #f0f0f0;
  }

  .phone-product-img {
    height: 46px;
    border-radius: 6px;
    background: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    margin-bottom: 5px;
  }

  .phone-product-name {
    font-size: 7.5px;
    color: #999;
    font-family: 'Outfit', sans-serif;
    margin-bottom: 2px;
  }

  .phone-product-price {
    font-size: 9px;
    font-weight: 600;
    color: #F59A5F;
    font-family: 'Outfit', sans-serif;
  }

  /* Floating pills */
  .pill-ios {
    position: absolute;
    top: 28px; right: -18px;
    background: #111;
    color: #fff;
    border-radius: 100px;
    padding: 8px 14px 8px 10px;
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 11px;
    font-weight: 500;
    font-family: 'Outfit', sans-serif;
    box-shadow: 0 8px 24px rgba(0,0,0,0.2);
    white-space: nowrap;
    animation: pillup 4s ease-in-out infinite;
  }

  .pill-android {
    position: absolute;
    bottom: 44px; left: -26px;
    background: #fff;
    color: #333;
    border-radius: 100px;
    padding: 8px 14px 8px 10px;
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 11px;
    font-weight: 500;
    font-family: 'Outfit', sans-serif;
    box-shadow: 0 8px 24px rgba(0,0,0,0.1);
    white-space: nowrap;
    border: 1px solid #eee;
    animation: pilldown 5s ease-in-out infinite;
  }

  .dot-green {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #22c55e;
    box-shadow: 0 0 6px #22c55e;
    flex-shrink: 0;
  }

  .dot-orange {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #F59A5F;
    flex-shrink: 0;
  }

  @keyframes pillup {
    0%,100% { transform: translateY(0); }
    50% { transform: translateY(-7px); }
  }

  @keyframes pilldown {
    0%,100% { transform: translateY(0); }
    50% { transform: translateY(7px); }
  }

  @media (max-width: 860px) {
    .app-main-card { grid-template-columns: 1fr; }
    .app-card-right { min-height: 320px; }
    .app-card-left { padding: 40px 28px; }
    .app-card-left::after { display: none; }
  }

  @media (max-width: 480px) {
    .app-sec { padding: 24px 16px 60px; }
    .app-btn-group { flex-direction: column; }
    .app-dl-btn { min-width: unset; width: 100%; }
  }
`;

const phoneItems = [
  { emoji: "👗", name: "Fashion", price: "৳1,299" },
  { emoji: "📱", name: "Gadgets", price: "৳8,999" },
  { emoji: "👟", name: "Shoes",   price: "৳2,499" },
  { emoji: "🎧", name: "Audio",   price: "৳4,599" },
];

export default function IMALLAppSection() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const ob = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) ob.observe(ref.current);
    return () => ob.disconnect();
  }, []);

  return (
    <>
      <style>{styles}</style>
      <section className="app-sec" ref={ref}>
        <div className="app-sec-inner">

          <div className="app-sec-label">GET THE APP</div>

          <div
            className="app-main-card"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(32px)",
              transition: "opacity 0.7s ease, transform 0.7s ease",
            }}
          >
            {/* Left */}
            <div className="app-card-left">
              <div>
                <h2 className="app-headline">
                  Your favourite brands,<br />
                  <em>in your pocket.</em>
                </h2>
                <p className="app-desc">
                  <span style={{color:"#F59A5F", fontWeight:600}}>iMall</span> started with a simple mission: to make{" "}
                  <strong>quality products accessible to everyone.</strong> We've grown
                  into a trusted platform connecting thousands of customers with
                  their favourite brands every day.
                </p>

                <div className="app-btn-group">
                  <a
                    href="https://apps.apple.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-dl-btn ios"
                  >
                    <svg className="btn-svg" viewBox="0 0 24 24">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <span className="btn-copy">
                      <span className="btn-copy-small">Download on the</span>
                      <span className="btn-copy-main">App Store</span>
                    </span>
                  </a>

                  <a
                    href="https://play.google.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-dl-btn android"
                  >
                    <svg className="btn-svg" viewBox="0 0 24 24">
                      <path d="M3.18 23.76c.3.17.64.24.99.2L14.64 12 3.18.04c-.35-.04-.69.03-.99.2C1.53.82 1.08 1.64 1.08 2.63v18.74c0 .99.45 1.81 2.1 2.39zM15.84 12l3.38 3.38-9.7 5.48 6.32-8.86zm0 0l-6.32-8.86 9.7 5.48L15.84 12zm4.45 6.97c.62-.47.98-1.08.98-1.74V6.77c0-.66-.36-1.27-.98-1.74l-4.27 6.97 4.27 6.97z"/>
                    </svg>
                    <span className="btn-copy">
                      <span className="btn-copy-small">Get it on</span>
                      <span className="btn-copy-main">Google Play</span>
                    </span>
                  </a>
                </div>
              </div>

              {/* Trust strip */}
              <div className="app-trust-strip">
                {[
                  { icon: "🛍️", num: "10K+",  label: "Customers"  },
                  { icon: "⭐",  num: "4.9",   label: "App Rating" },
                  { icon: "🏷️", num: "500+",  label: "Brands"     },
                ].map(t => (
                  <div key={t.label} className="app-trust-item">
                    <div className="app-trust-icon">{t.icon}</div>
                    <div className="app-trust-text">
                      <strong>{t.num}</strong>
                      <span>{t.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — phone mockup */}
            <div className="app-card-right">
              <div className="app-phone-wrap">
                <div className="app-phone-shell">
                  <div className="app-phone-notch" />
                  <div className="app-phone-screen">
                    <div style={{ height: "18px" }} />
                    <div className="phone-header">
                      <div className="phone-logo" style={{color:"#F59A5F"}}>i<span>Mall</span></div>
                      <div className="phone-dot" />
                    </div>
                    <div className="phone-search">🔍 Search products...</div>
                    <div className="phone-tags">
                      {["All", "Fashion", "Tech", "Home"].map(t => (
                        <div key={t} className="phone-tag">{t}</div>
                      ))}
                    </div>
                    <div className="phone-products">
                      {phoneItems.map(p => (
                        <div key={p.name} className="phone-product">
                          <div className="phone-product-img">{p.emoji}</div>
                          <div className="phone-product-name">{p.name}</div>
                          <div className="phone-product-price">{p.price}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="pill-ios">
                  <span className="dot-green" />
                  iOS App Live
                </div>
                <div className="pill-android">
                  <span className="dot-orange" />
                  Android Ready
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
}